import { Box, Stack } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ImageInput from "../../components/forms/ImageInput";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Text from "../../components/ui/Text";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import apiService from "../../services/api";
import { validateImage } from "../../utils/imageUpload";

const AccountSetup = () => {
  const navigate = useNavigate();
  const { currentUser, fetchUserProfile } = useAuth();
  const { showToast } = useToast();

  const from = sessionStorage.getItem("postSignupRedirect");

  const [formData, setFormData] = useState({
    firstName: currentUser?.first_name || "",
    lastName: currentUser?.last_name || "",
    bio: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isLoadingProfile] = useState(false);

  // Get user's first name from multiple sources
  const getUserFirstName = () => {
    // 1. Try from form input (if user is typing)
    if (formData.firstName.trim()) {
      return formData.firstName;
    }

    // 3. Default fallback
    return "there";
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  const handleImageChange = async (file) => {
    if (!file) {
      return;
    }

    // Validate the image
    const validation = validateImage(file);
    if (!validation.isValid) {
      showToast(validation.error, "error");
      return;
    }

    try {
      const response = await apiService.uploadProfileImage(file);
      if (response.success) {
        await fetchUserProfile(currentUser?.accessToken);
        showToast(response.message, "success");
      }
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      setError("");

      // Prepare account setup data for API (using email as identifier)
      const accountSetupData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        bio: formData.bio,
        profile_setup_completed: true,
      };

      // Direct API call to your existing endpoint
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser?.accessToken}`,
        },
        body: JSON.stringify(accountSetupData),
      });
      fetchUserProfile(currentUser?.accessToken);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to update profile");
      }

      if (from) {
        navigate(from, {
          replace: true,
          state: {
            from: window.location.pathname,
            currentUserId: currentUser?.id,
          },
        });
        return;
      }

      // Navigate to dashboard with new user flag
      navigate("/dashboard", { state: { isNewUser: true } });
    } catch (error) {
      setError(error.message || "Failed to update profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          py: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Text
            variant="pageHeader"
            sx={{
              mb: 2,
            }}
          >
            Welcome, {getUserFirstName()}!
          </Text>
          <Text
            variant="paragraph"
            sx={{
              color: "#000",
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            Finish your profile set up to continue.
          </Text>
        </Box>

        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={3} sx={{ maxWidth: "500px", mx: "auto" }}>
            {/* Error Message */}
            {error && (
              <Box
                sx={{
                  backgroundColor: "#FFF2F0",
                  border: "1px solid #FFCCC7",
                  borderRadius: "4px",
                  padding: "12px",
                }}
              >
                <Text
                  variant="paragraph3"
                  sx={{
                    color: "#D32F2F",
                  }}
                >
                  {error}
                </Text>
              </Box>
            )}

            {/* Profile Image */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
              }}
            >
              <ImageInput
                value={currentUser?.profile_image_url}
                onChange={handleImageChange}
                firstName={formData.firstName}
                lastName={formData.lastName}
              />
            </Box>

            {/* Form Fields */}
            <Input
              label="First name"
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              required
              disabled={isLoadingProfile}
            />

            <Input
              label="Last name"
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              required
              disabled={isLoadingProfile}
            />

            <Input
              label="Bio (optional)"
              value={formData.bio}
              onChange={handleInputChange("bio")}
              multiline
              rows={3}
              placeholder="Tell us a bit about yourself..."
              disabled={isLoadingProfile}
            />

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || isLoadingProfile}
              sx={{
                width: "100%",
                mt: 3,
              }}
            >
              {isLoadingProfile
                ? "Loading profile..."
                : isSubmitting
                  ? "Setting up..."
                  : "Complete setup"}
            </Button>
          </Stack>
        </form>
      </Box>
    </>
  );
};

export default AccountSetup;
